import React, { useCallback, useEffect, useRef, useState } from "react"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { useForm } from "react-hook-form"
import { Outlet } from "react-router-dom"
import { AdminProfileService } from "services/api-service"
import { AdminProfileType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box } from "ui/components/atoms/_atoms"
import Input from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import { P } from "ui/components/atoms/typography"
import { Pagination } from "ui/components/molecules/pagination"
import { PROFILESTATUS_ARRAY } from "ui/data/ProfileInformations"
import ProfilesList from "views/Admin/Profiles/ProfilesList"

const Profiles = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    unregister,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      q: "",
      ststus: "all",
    },
  })

  const q = watch("q")
  const ststus = watch("ststus")

  const firstRender = useRef<boolean>(true)
  const timeoutId = useRef<NodeJS.Timeout | null>(null)
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [page, setPage] = useState(0)
  const [data, setData] = useState<AdminProfileType[]>([])

  const fetchProfiles = useCallback(
    async (newPage: number) => {
      try {
        setTotalCount(0)
        setLoading(true)
        const { content, totalElements, totalPages } = await AdminProfileService.getProfiles(
          q,
          ststus,
          newPage,
          15,
        )
        setTotalCount(totalElements)
        setTotalPages(totalPages)
        setData(content)
      } catch (e) {
        SentryService.error("[ERROR AdminProfileService.getProfiles]:", e)
      } finally {
        setLoading(false)
      }
    },
    [q, ststus],
  )

  useEffect(() => {
    fetchProfiles(page)
  }, [fetchProfiles, page])

  const handleOnRefresh = useCallback(() => {
    setPage(0)
    fetchProfiles(0)
  }, [fetchProfiles])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }

    timeoutId.current = setTimeout(() => {
      handleOnRefresh()
    }, 1000)
  }, [q, ststus, handleOnRefresh])

  return (
    <>
      <PanelLayoutContent title="Lista profili" text="Lista aktualnych profili na platformie">
        <FiltersWrapper $marginBottom="M">
          <Input name="q" label="Szukaj" placeholder="np. nazwa firmy" rhf={register("q")} />
          <Select
            name="ststus"
            label="Status*"
            options={[["Wszystkie", "all"], ...PROFILESTATUS_ARRAY]}
            value={watch("ststus")}
            rhf={register("ststus")}
            $minWidth="200px"
          />
        </FiltersWrapper>
        {totalCount > 0 && (
          <P>
            System znalazł <span>{totalCount}</span> profili
          </P>
        )}
        <ProfilesList loading={loading} data={data} />
        {data.length > 0 && (
          <Pagination
            defaultPage={page + 1}
            totalPages={totalPages}
            onChange={e => setPage(e - 1)}
          />
        )}
      </PanelLayoutContent>
      <Outlet />
    </>
  )
}

export default Profiles

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.S};
`
